import { useRouter } from 'next/router';
import React from 'react';
import { useTranslationContext } from '@/context/translation';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
	useVideoUploadDropzone,
} from '@/hooks';
import { getCurrentLandingPath, isPageLocalized } from '@/helpers';
import { reviewsLanding } from '@/data';
import type { LandingConfigItem } from '@/types';
import { AMPLITUDE_EVENTS, DEFAULT_LOCALE } from '@/constants';
import { Reviews } from '../reusableSections';
import { Benefits } from './Benefits';
import { FAQ } from './FAQ';
import { HeroVideoUpload } from './HeroVideoUpload';
import { HowItWorks } from './HowItWorks';
import { ToolsWidget } from './ToolsWidget';
import { TryLift } from './TryLift';
import { WhyLift } from './WhyLift';

interface Props {
	data: LandingConfigItem;
	funnelLinkService?: string;
}

export const VideoLandingTemplate: React.FC<Props> = ({
	data,
	funnelLinkService,
}) => {
	const router = useRouter();
	const { locale, translations } = useTranslationContext();

	const currentPath = getCurrentLandingPath(router);
	const isLocalized = isPageLocalized(currentPath);
	const reviewsLocale = isLocalized ? locale : DEFAULT_LOCALE;

	const { t } = useTranslationWithLocale(
		data.translationLocale,
		locale,
		translations?.[data.translationLocale] as
			| Record<string, unknown>
			| undefined,
	);
	const { t: tShared } = useTranslationWithLocale('sharedLandingData', locale);
	const { t: tReviews } = useTranslationWithLocale(
		'landing-reviews',
		reviewsLocale,
		translations?.['landing-reviews'] as Record<string, unknown> | undefined,
	);

	const { dropzoneOptions, isUploading, progress, uploadingMediaType } =
		useVideoUploadDropzone({
			galleryContext: data.analytics.galleryContext,
			funnelLinkService,
			acceptImages: data.acceptImagesInDropzone,
			mediaCookieKey: data.mediaCookieKey,
		});

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		data.analytics.scrolledPercentName,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: data.analytics.eventPropertyType },
	);
	useLandingShownEvent({ type: data.analytics.eventPropertyType });

	if (data.hero.type !== 'withVideoUpload') return null;

	return (
		<>
			<HeroVideoUpload
				data={data.hero}
				t={t}
				dropzoneOptions={dropzoneOptions}
				isLoading={isUploading}
				uploadPercentage={progress.percentage}
				withLoadingOverlay={uploadingMediaType !== 'image'}
			/>

			{data.howItWork && (
				<HowItWorks
					data={data.howItWork}
					t={t}
					dropzoneOptions={dropzoneOptions}
					withDropzone={data.withDropZone}
					funnelLinkService={funnelLinkService}
					useEmailFlow={false}
				/>
			)}

			<Reviews t={tReviews} data={reviewsLanding} />

			{data.benefits && (
				<Benefits
					data={data.benefits}
					t={t}
					dropzoneOptions={dropzoneOptions}
					withDropzone={data.withDropZone}
					funnelLinkService={funnelLinkService}
					useEmailFlow={false}
				/>
			)}

			{data.faq && (
				<FAQ
					data={data.faq}
					t={t}
					funnelLinkService={funnelLinkService}
					withDropzone={data.withDropZone}
					dropzoneOptions={dropzoneOptions}
					useEmailFlow={false}
				/>
			)}

			{data.whyLift && (
				<WhyLift
					data={data.whyLift}
					t={t}
					dropzoneOptions={dropzoneOptions}
					withDropzone={data.withDropZone}
					funnelLinkService={funnelLinkService}
					useEmailFlow={false}
				/>
			)}

			{!data.hideToolsWidget && data.explore && (
				<ToolsWidget data={data.explore} t={tShared} />
			)}

			{data.tryLift && <TryLift data={data.tryLift} t={tShared} />}
		</>
	);
};
